import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Button, Drawer, Spin, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { GoogleMap, Marker, Polygon, useJsApiLoader } from '@react-google-maps/api';
import {
    GET_DSP_TASK_DETAIL_WITH_CHILDREN,
    GET_DSP_TASK_DETAIL_WITH_CHILDREN_FOR_DSP,
    DspTaskDetailWithChildrenOutput,
} from '../graphql/getDspTaskDetailWithChildren';
import { GET_WAREHOUSE_BY_ID, GetWarehouseByIdResponse } from '../../../../graphql/queries/warehouseQueries';
import { AssignType } from '../graphql/assignDrivers';
import { getAssignTypeLabel } from '../utils/assignTypeLabel';
import { useTranslation } from 'react-i18next';
import {
    GET_ZIPCODE_MAP_BOUNDARIES_BY_AREA_ID,
    GetZipcodeMapBoundariesByAreaIdResponse,
    GetZipcodeMapBoundariesByAreaIdVariables,
} from '../graphql/getZipcodeMapBoundariesByAreaId';


const { Text } = Typography;

const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

const SEGMENT_ACCENT_COLORS = [
    // 基础色
    '#3B82F6', '#F97316', '#22C55E', '#EF4444', '#EC4899',
    '#A855F7', '#EAB308', '#06B6D4', '#6366F1', '#14B8A6',
    '#F43F5E', '#84CC16',
    // 扩展色
    '#CE4422', '#2244CE', '#A6CE22', '#CE22CB', '#22CE8F',
    '#E09C3E', '#513EE0', '#79E03E', '#E03EB4', '#3EA2E0',
    '#CEC022', '#4D22CE', '#36CE22', '#CE225B', '#546378',
    '#D2E03E', '#BA3EE0', '#3EE054',
    // 深/灰调扩展
    '#8C5A2B', '#21407A', '#B3A36E', '#5E2E6E', '#1F5C3A',
    '#7C2E3E', '#205E5E', '#9E6B8C', '#6E7A2E', '#3C424B',
];
const SEGMENT_COLOR_PALETTE = SEGMENT_ACCENT_COLORS.map(accent => ({
    accent,
    bg: accent + '3B',
    border: accent + '4D',
}));

/**
 * 地址格式化
 */
const formatAddress = (addr?: { streetAddress?: string | null; city?: string | null; state?: string | null; zipCode?: string | null } | null): string => {
    if (!addr) return '—';
    return [addr.streetAddress, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ') || '—';
};

interface SplitRouteHistoryDrawerProps {
    visible: boolean;
    dspTaskId: number | null;
    warehouseId: number | undefined;
    areaId?: number | null;
    mode?: 'wms' | 'dsp';
    onClose: () => void;
}

const SplitRouteHistoryDrawer: React.FC<SplitRouteHistoryDrawerProps> = ({
    visible, dspTaskId, warehouseId, areaId, onClose, mode = 'dsp',
}) => {
    const { t } = useTranslation();

    /**
     * 获取历史数据
     */
    const taskDetailQuery = mode === 'wms' ? GET_DSP_TASK_DETAIL_WITH_CHILDREN : GET_DSP_TASK_DETAIL_WITH_CHILDREN_FOR_DSP;
    const taskDetailKey = mode === 'wms' ? 'getDspTaskDetailWithChildren' : 'getDspTaskDetailWithChildrenForDsp';
    const { data, loading } = useQuery(
        taskDetailQuery,
        {
            variables: {
                request: {
                    warehouseId: warehouseId!,
                    dspTaskId: Number(dspTaskId),
                },
            },
            skip: !visible || !warehouseId || !dspTaskId,
            fetchPolicy: 'network-only',
        }
    );

    /**
     * 获取WHS信息
     */
    const { data: warehouseData } = useQuery<GetWarehouseByIdResponse>(GET_WAREHOUSE_BY_ID, {
        variables: { id: warehouseId },
        skip: !visible || !warehouseId,
    });

    /**
     * 获取WHS经纬度
     */
    const warehouseFallbackCenter = useMemo(() => {
        const loc = warehouseData?.warehouse?.location;
        if (loc?.latitude != null && loc?.longitude != null) return { lat: loc.latitude, lng: loc.longitude };
        return null;
    }, [warehouseData]);

    const taskDetail: DspTaskDetailWithChildrenOutput | null = (data as any)?.[taskDetailKey] ?? null;
    const assignType = taskDetail?.assignType ?? null;
    // 直接指派或未拆分时，childTaskList 为空，订单在父任务本身上
    const children = useMemo<DspTaskDetailWithChildrenOutput[]>(() => {
        return (taskDetail?.childTaskList?.length ?? 0) > 0
            ? (taskDetail?.childTaskList ?? [])
            : taskDetail ? [taskDetail] : [];
    }, [taskDetail]);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        preventGoogleFontsLoading: true,
    });

    const mapRef = useRef<google.maps.Map | null>(null);
    const hasFitRef = useRef(false);

    // 地图图层开关
    const [showZipcodeBoundary, setShowZipcodeBoundary] = useState(true);
    const [showNavTrack, setShowNavTrack] = useState(false);
    const [hideMapLabels, setHideMapLabels] = useState(true);
    const [showParcelLocation, setShowParcelLocation] = useState(true);
    const [showStartPoint,     setShowStartPoint]     = useState(true);
    const [showEndPoint,       setShowEndPoint]        = useState(true);
    const [panExpanded, setPanExpanded] = useState(false);

    // map 引用（useState 以支持 useEffect 依赖）
    const [map, setMap] = useState<google.maps.Map | null>(null);

    const [hoveredZipcodeBoundary, setHoveredZipcodeBoundary] = useState<{
        zipcode: string; x: number; y: number;
    } | null>(null);

    /**
     * Zipcode 围栏：根据 areaId 查询边界数据
     */
    const { data: boundariesData } = useQuery<GetZipcodeMapBoundariesByAreaIdResponse, GetZipcodeMapBoundariesByAreaIdVariables>(
        GET_ZIPCODE_MAP_BOUNDARIES_BY_AREA_ID,
        {
            variables: { areaId: areaId! },
            skip: !visible || !areaId,
            fetchPolicy: 'cache-and-network',
        }
    );

    interface ZipcodeBoundaryEntry {
        zipcode: string;
        paths: google.maps.LatLngLiteral[];
    }

    const zipcodeBoundaryData = useMemo((): ZipcodeBoundaryEntry[] => {
        const items = boundariesData?.getZipcodeMapBoundariesByAreaId;
        if (!items?.length) return [];
        const entries: ZipcodeBoundaryEntry[] = [];
        for (const item of items) {
            try {
                const coords = JSON.parse(item.boundary);
                const rawPath = Array.isArray(coords?.[0]) ? coords[0] : coords;
                const path = (rawPath as any[])
                    .filter((p: any) => typeof p.lat === 'number' && typeof p.lng === 'number')
                    .map((p: any) => ({ lat: p.lat, lng: p.lng }));
                if (path.length === 0) continue;
                entries.push({ zipcode: item.zipcode, paths: path });
            } catch (_e) {
                // 解析失败静默跳过
            }
        }
        return entries;
    }, [boundariesData]);

    // 动态加载 geometry 库
    useEffect(() => {
        if (isLoaded) {
            (google.maps as any).importLibrary?.('geometry').catch(() => {});
        }
    }, [isLoaded]);

    const handleMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
        if (!showZipcodeBoundary || !e.latLng || !zipcodeBoundaryData.length) {
            if (hoveredZipcodeBoundary) setHoveredZipcodeBoundary(null);
            return;
        }
        const latLng = e.latLng;
        const domEvent = e.domEvent as MouseEvent;
        let found = false;
        for (const entry of zipcodeBoundaryData) {
            if (entry.paths.length >= 3 && google.maps.geometry?.poly?.containsLocation?.(
                latLng, new google.maps.Polygon({ paths: entry.paths })
            )) {
                setHoveredZipcodeBoundary({
                    zipcode: entry.zipcode,
                    x: domEvent.clientX,
                    y: domEvent.clientY,
                });
                found = true;
                break;
            }
        }
        if (!found && hoveredZipcodeBoundary) setHoveredZipcodeBoundary(null);
    }, [showZipcodeBoundary, zipcodeBoundaryData, hoveredZipcodeBoundary]);

    /**
     * 整合所有坐标点
     */
    const allCoords = useMemo(() => {
        const pts: google.maps.LatLngLiteral[] = [];
        children.forEach(child => {
            (child.routePlanningOrderList ?? []).forEach(o => {
                if (o.latitude != null && o.longitude != null) pts.push({ lat: o.latitude, lng: o.longitude });
            });
            // 仅地图圈选模式的 assignTypeDetail 是坐标，stop/zipcode 不是
            if (assignType === AssignType.BY_MAP) {
                (child.assignTypeDetail ?? []).forEach(fenceStr => {
                    fenceStr.split(';').forEach(s => {
                        const [lat, lng] = s.split(',').map(Number);
                        if (!isNaN(lat) && !isNaN(lng)) pts.push({ lat, lng });
                    });
                });
            }
            if (child.startAddress?.latitude != null && child.startAddress?.longitude != null)
                pts.push({ lat: child.startAddress.latitude, lng: child.startAddress.longitude });
            if (child.endAddress?.latitude != null && child.endAddress?.longitude != null)
                pts.push({ lat: child.endAddress.latitude, lng: child.endAddress.longitude });
        });
        return pts;
    }, [children]);

    // 数据就绪后重置 fit 标记，让下次 onIdle 重新 fit
    useEffect(() => {
        if (allCoords.length > 0) hasFitRef.current = false;
    }, [allCoords]);

    /**
     * 初始化渲染
     */
    const handleMapIdle = () => {
        if (!mapRef.current || allCoords.length === 0 || hasFitRef.current) return;
        hasFitRef.current = true;
        const bounds = new google.maps.LatLngBounds();
        allCoords.forEach(p => bounds.extend(p));
        mapRef.current.fitBounds(bounds, 60);
    };

    /**
     * 水滴Marker
     */
    const orderMarkers = useMemo(() => {
        if (!isLoaded) return [];
        return children.flatMap((child, idx) => {
            const palette = SEGMENT_COLOR_PALETTE[idx % SEGMENT_COLOR_PALETTE.length];
            return (child.routePlanningOrderList ?? [])
                .filter(o => o.latitude != null && o.longitude != null)
                .map(o => {
                    const label = o.stopNumber != null ? String(o.stopNumber) : '';
                    const icon: google.maps.Icon = {
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 36 46">
                                <path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 28 14 28s14-17.5 14-28c0-7.7-6.3-14-14-14z" fill="${palette.accent}" stroke="white" stroke-width="1.5"/>
                                ${label ? `<text x="18" y="20" font-size="10" fill="white" text-anchor="middle" font-weight="bold" font-family="Arial">${label}</text>` : ''}
                            </svg>
                        `),
                        scaledSize: { width: 36, height: 46 } as google.maps.Size,
                        anchor: { x: 18, y: 46 } as google.maps.Point,
                    };
                    return <Marker key={o.orderId} position={{ lat: o.latitude!, lng: o.longitude! }} icon={icon} clickable={false} />;
                });
        });
    }, [isLoaded, children]);

    /**
     * 多边形（地图拆分模式）
     */
    const mapPolygons = useMemo(() => {
        if (assignType !== AssignType.BY_MAP) return [];
        return children.flatMap((child, idx) => {
            const palette = SEGMENT_COLOR_PALETTE[idx % SEGMENT_COLOR_PALETTE.length];
            return (child.assignTypeDetail ?? []).map(fenceStr => {
                const paths = fenceStr.split(';').map(s => {
                    const [lat, lng] = s.split(',').map(Number);
                    return { lat, lng };
                }).filter(p => !isNaN(p.lat) && !isNaN(p.lng));
                return { child, idx, palette, paths };
            });
        }).filter(item => item.paths.length >= 3);
    }, [assignType, children]);

    /**
     * 起终点
     */
    const addressMarkers = useMemo(() => {
        if (!isLoaded) return [];
        const result: { key: string; position: google.maps.LatLngLiteral; accent: string; type: 'start' | 'end' }[] = [];
        children.forEach((child, idx) => {
            const { accent } = SEGMENT_COLOR_PALETTE[idx % SEGMENT_COLOR_PALETTE.length];
            if (child.startAddress?.latitude != null && child.startAddress?.longitude != null)
                result.push({ key: `s-${child.id ?? idx}`, position: { lat: child.startAddress.latitude, lng: child.startAddress.longitude }, accent, type: 'start' });
            if (child.endAddress?.latitude != null && child.endAddress?.longitude != null)
                result.push({ key: `e-${child.id ?? idx}`, position: { lat: child.endAddress.latitude, lng: child.endAddress.longitude }, accent, type: 'end' });
        });
        return result;
    }, [isLoaded, children]);

    /**
     * 导航线路数据：每个分段一条 Polyline，按 stopNumber 排序连线
     */
    const routePolylines = useMemo((): Array<{ path: google.maps.LatLngLiteral[]; color: string }> | null => {
        const lines: Array<{ path: google.maps.LatLngLiteral[]; color: string }> = [];
        children.forEach((child, idx) => {
            const orders = (child.routePlanningOrderList ?? [])
                .filter(o => o.latitude != null && o.longitude != null && o.stopNumber != null)
                .sort((a, b) => a.stopNumber! - b.stopNumber!);
            if (orders.length === 0) return;
            const path: google.maps.LatLngLiteral[] = [];
            if (child.startAddress?.latitude != null && child.startAddress?.longitude != null) {
                path.push({ lat: child.startAddress.latitude, lng: child.startAddress.longitude });
            }
            orders.forEach(o => path.push({ lat: o.latitude!, lng: o.longitude! }));
            if (child.endAddress?.latitude != null && child.endAddress?.longitude != null) {
                path.push({ lat: child.endAddress.latitude, lng: child.endAddress.longitude });
            }
            if (path.length > 1) {
                lines.push({ path, color: SEGMENT_COLOR_PALETTE[idx % SEGMENT_COLOR_PALETTE.length].accent });
            }
        });
        return lines.length > 0 ? lines : null;
    }, [children]);

    // 命令式管理路径连线 Polyline
    const routePolylinesRef = useRef<google.maps.Polyline[]>([]);
    useEffect(() => {
        routePolylinesRef.current.forEach(p => p.setMap(null));
        routePolylinesRef.current = [];
        if (!map || !showNavTrack || !routePolylines) return;
        routePolylinesRef.current = routePolylines.map(line =>
            new google.maps.Polyline({
                path: line.path,
                strokeColor: line.color,
                strokeWeight: 2,
                strokeOpacity: 0.7,
                clickable: false,
                map,
            })
        );
        return () => {
            routePolylinesRef.current.forEach(p => p.setMap(null));
            routePolylinesRef.current = [];
        };
    }, [map, showNavTrack, routePolylines]);

    /**
     * 地图中心点
     */
    const mapCenter = useMemo(() => {
        if (mapPolygons.length > 0 && mapPolygons[0].paths.length > 0) return mapPolygons[0].paths[0];
        const allOrders = children.flatMap(c => c.routePlanningOrderList ?? []);
        const firstWithCoord = allOrders.find(o => o.latitude != null && o.longitude != null);
        if (firstWithCoord) return { lat: firstWithCoord.latitude!, lng: firstWithCoord.longitude! };
        if (addressMarkers.length > 0) return addressMarkers[0].position;
        return warehouseFallbackCenter ?? { lat: 0, lng: 0 };
    }, [mapPolygons, children, addressMarkers, warehouseFallbackCenter]);

    const assignTypeLabel = getAssignTypeLabel(assignType);

    /**
     * 渲染分段数据
     */
    const renderSegmentCard = (child: DspTaskDetailWithChildrenOutput, idx: number) => {
        const palette = SEGMENT_COLOR_PALETTE[idx % SEGMENT_COLOR_PALETTE.length];
        const parcelCount = child.routePlanningOrderList?.length ?? child.totalParcels ?? 0;

        let detail: React.ReactNode = null;
        if (assignType === AssignType.BY_MAP) {
            detail = child.parcelNumberStart != null && child.parcelNumberEnd != null
                ? <span>Stop {child.parcelNumberStart}-{child.parcelNumberEnd}</span>
                : null;
        } else if (assignType === AssignType.BY_ZIPCODE) {
            const zips = child.assignTypeDetail ?? [];
            detail = zips.length > 0 ? <span>{zips.join(', ')}</span> : null;
        } else if (assignType === AssignType.BY_STOP) {
            const ranges = child.assignTypeDetail ?? [];
            detail = ranges.length > 0
                ? <span>Stop {ranges.map(r => r.replace(',', '-')).join(' ')}</span>
                : (child.parcelNumberStart != null && child.parcelNumberEnd != null
                    ? <span>Stop {child.parcelNumberStart}-{child.parcelNumberEnd}</span>
                    : null);
        } else if (assignType === AssignType.BY_ALGORITHM) {
            detail = child.parcelNumberStart != null && child.parcelNumberEnd != null
                ? <span>Stop {child.parcelNumberStart}-{child.parcelNumberEnd}</span>
                : null;
        }

        return (
            <div key={child.id ?? idx} className="rounded-xl border p-4 mb-3" style={{ borderColor: palette.border, backgroundColor: palette.bg }}>
                {/* 分段标题行 */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: palette.accent }} />
                    <Text strong className="text-base">{child.taskName ?? t('splitOrder.history.segment', { index: idx + 1 })}</Text>
                    <span className="ml-auto text-sm" style={{ color: palette.accent }}>{t('splitOrder.history.parcels', { count: parcelCount })}</span>
                </div>
                {/* 详情 */}
                <div className="space-y-2 text-base text-gray-600 pl-5">
                    {detail && (
                        <div className="text-gray-500 font-medium">{detail}</div>
                    )}
                    <div className="flex items-center gap-2">
                        <span className="text-gray-400 shrink-0">{t('splitOrder.history.driver')}</span>
                        <span className={`font-medium ${child.driverId != null && Number(child.driverId) < 0 ? 'text-red-500' : 'text-gray-700'}`}>
                            {child.driverId != null && Number(child.driverId) < 0 ? t('delivery.tasks.label.unassignedDriver') : (child.driverName ?? '—')}
                        </span>
                    </div>
                    {child.workIndex ? (
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 shrink-0">{t('splitOrder.history.workIndex')}</span>
                            <span className="text-gray-700">{child.workIndex}</span>
                        </div>
                    ) : null}
                    {assignType === AssignType.BY_ALGORITHM && (() => {
                        const rawDetail = child.assignTypeDetail ?? [];
                        const first = rawDetail[0];
                        const hasCapacity = first && /^\d+,\d+$/.test(first);
                        const [min, max] = hasCapacity ? first.split(',') : [];
                        const zipcodes = hasCapacity ? rawDetail.slice(1) : rawDetail;
                        return (
                            <>
                                {hasCapacity && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-400 shrink-0">{t('splitOrder.history.driverCapacity')}</span>
                                        <span className="text-gray-700">{min} ~ {max}</span>
                                    </div>
                                )}
                                {zipcodes.length > 0 && (
                                    <div className="flex items-start gap-2">
                                        <span className="text-gray-400 shrink-0">{t('splitOrder.history.preferredZipcodes')}</span>
                                        <span className="text-gray-700">{zipcodes.join(', ')}</span>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                    <div className="flex items-start gap-2">
                        <span className="text-gray-400 shrink-0">{t('splitOrder.history.startPoint')}</span>
                        <span className="text-gray-600">{formatAddress(child.startAddress)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <span className="text-gray-400 shrink-0">{t('splitOrder.history.endPoint')}</span>
                        <span className="text-gray-600">{formatAddress(child.endAddress)}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Drawer
            open={visible}
            onClose={onClose}
            placement="right"
            width="100vw"
            closable={false}
            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
        >
            {/* ===== 顶部信息栏（与 SplitRouteDrawer 相同布局）===== */}
            <div className="flex py-5 border-b border-gray-200 bg-white shrink-0">

                {/* 第一块：返回 + 区域名称 */}
                <div className="flex-[3] pl-4 self-start flex items-center gap-3">
                    <Button
                        type="text"
                        onClick={onClose}
                        style={{ color: '#4f46e5', padding: '0 4px' }}
                        icon={
                            <ArrowLeftOutlined style={{
                                fontSize: 22,
                                color: '#4f46e5',
                                filter: 'drop-shadow(0 0 0.5px #4f46e5) drop-shadow(0 0 0.5px #4f46e5)',
                            }} />
                        }
                    />
                    <div className="border-l-4 border-indigo-600 pl-2">
                        <Text className="text-xl font-bold">{t('splitOrder.history.lastRecord', { name: taskDetail?.taskName ?? '—' })}</Text>
                    </div>
                </div>

                {/* 第二块：包裹统计 */}
                <div className="flex-[2] flex justify-center items-center text-base">
                    <div className="flex flex-col gap-2">
                        <Text className="text-gray-600">
                            {t('splitOrder.history.areaTotalParcels')}&nbsp;<Text strong className="text-xl">{taskDetail?.totalParcels ?? 0}</Text>
                        </Text>
                        <Text className="ttext-gray-600">
                            {t('splitOrder.history.assignMethod')}&nbsp;<Text strong>{assignTypeLabel}</Text>
                        </Text>
                    </div>
                </div>

                {/* 第三块：分段统计 */}
                <div className="flex-[2] flex flex-col gap-3 justify-center items-center text-base">
                    <div className="flex flex-col gap-2">
                        <Text className="ttext-gray-600">
                            {t('splitOrder.history.splitSegments')}&nbsp;<Text strong className="text-xl">{children.length}</Text>
                        </Text>
                        <Text className="ttext-gray-600">
                            {t('splitOrder.history.assignedDrivers')}&nbsp;<Text strong>{new Set(children.map(c => c.driverId).filter(Boolean)).size}</Text>
                        </Text>
                    </div>
                </div>

                {/* 第四块：操作按钮 */}
                <div className="flex-[4] flex justify-end items-center gap-12 px-16">
                    <Button onClick={onClose}>{t('splitOrder.history.back')}</Button>
                </div>
            </div>

            {/* ===== 主体区域 ===== */}
            <div className="flex flex-1 overflow-hidden relative">

                {/* 左侧分段列表 */}
                <div className="flex-[25] border-r border-gray-200 overflow-y-auto flex flex-col bg-white">
                    <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-32"><Spin /></div>
                        ) : children.length === 0 ? (
                            <div className="text-center text-gray-300 text-sm py-12">{t('splitOrder.history.noData')}</div>
                        ) : (
                            children.map((child, idx) => renderSegmentCard(child, idx))
                        )}
                    </div>
                </div>

                {/* 右侧地图 */}
                <div className="flex-[75] relative">
                    {!isLoaded ? (
                        <div className="flex items-center justify-center h-full">
                            <Spin size="large" tip="Loading Map..." />
                        </div>
                    ) : (
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={mapCenter}
                            zoom={13}
                            onLoad={m => { mapRef.current = m; setMap(m); }}
                            onIdle={handleMapIdle}
                            onMouseMove={handleMouseMove}
                            options={{
                                disableDefaultUI: true,
                                zoomControl: false,
                                disableDoubleClickZoom: true,
                                styles: hideMapLabels ? [
                                    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                    { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                    { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                    { featureType: 'landscape', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                    { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                ] : [],
                            }}
                        >
                            {mapPolygons.map(({ child, idx, palette, paths }) => (
                                <Polygon
                                    key={child.id ?? idx}
                                    paths={paths}
                                    options={{
                                        fillColor: palette.accent,
                                        fillOpacity: 0.2,
                                        strokeColor: palette.accent,
                                        strokeWeight: 2,
                                        clickable: false,
                                        editable: false,
                                    }}
                                />
                            ))}
                            {/* Zipcode 围栏叠加层 */}
                            {showZipcodeBoundary && zipcodeBoundaryData.map(entry => {
                                const isHovered = hoveredZipcodeBoundary?.zipcode === entry.zipcode;
                                return (
                                    <Polygon
                                        key={`zb-${entry.zipcode}`}
                                        paths={entry.paths}
                                        options={{
                                            fillColor: '#7c3aed',
                                            fillOpacity: isHovered ? 0.3 : 0.12,
                                            strokeColor: '#7c3aed',
                                            strokeOpacity: isHovered ? 1 : 0.6,
                                            strokeWeight: isHovered ? 2.5 : 1.5,
                                            zIndex: 1,
                                            clickable: false,
                                        }}
                                    />
                                );
                            })}

                            {showParcelLocation && orderMarkers}
                            {addressMarkers.filter(m => m.type === 'start' ? showStartPoint : showEndPoint).map(m => (
                                <Marker
                                    key={m.key}
                                    position={m.position}
                                    clickable={false}
                                    icon={{
                                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                                            `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="28" viewBox="0 0 32 28"><polygon points="16,2 30,26 2,26" fill="${m.accent}" stroke="${m.type === 'start' ? '#22c55e' : '#ef4444'}" stroke-width="3" stroke-linejoin="round"/></svg>`
                                        ),
                                        scaledSize: { width: 32, height: 28 } as google.maps.Size,
                                        anchor: { x: 16, y: 28 } as google.maps.Point,
                                    }}
                                />
                            ))}


                            {/* 自定义地图控件：右上角缩放 + 方向 */}
                            {/* Zipcode 围栏 hover tooltip */}
                            {hoveredZipcodeBoundary && (
                                <div
                                    style={{
                                        position: 'fixed',
                                        left: hoveredZipcodeBoundary.x + 12,
                                        top: hoveredZipcodeBoundary.y - 40,
                                        pointerEvents: 'none',
                                        zIndex: 1000,
                                    }}
                                >
                                    <div style={{
                                        background: 'rgba(0,0,0,0.75)',
                                        color: '#fff',
                                        padding: '6px 10px',
                                        borderRadius: 6,
                                        fontSize: 13,
                                        lineHeight: '1.5',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        <div style={{ fontWeight: 600 }}>{hoveredZipcodeBoundary.zipcode}</div>
                                    </div>
                                </div>
                            )}

                            <div className="absolute top-3 right-3 z-10 flex flex-col items-center gap-1.5 pointer-events-auto">
                                {/* 方向控件 */}
                                <div className="flex flex-col items-center">
                                    {panExpanded && (
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 mb-0.5"
                                            onClick={() => mapRef.current?.panBy(0, -80)}
                                        >▲</button>
                                    )}
                                    <div className="flex items-center gap-0.5">
                                        {panExpanded && (
                                            <button
                                                className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                onClick={() => mapRef.current?.panBy(-80, 0)}
                                            >◀</button>
                                        )}
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-500 hover:bg-gray-50"
                                            title={t('splitOrder.history.directionControl') ?? ''}
                                            onClick={() => setPanExpanded(v => !v)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                <polygon points="8,1 10,5 8,4 6,5"/>
                                                <polygon points="8,15 10,11 8,12 6,11"/>
                                                <polygon points="1,8 5,6 4,8 5,10"/>
                                                <polygon points="15,8 11,6 12,8 11,10"/>
                                                <circle cx="8" cy="8" r="1.5"/>
                                            </svg>
                                        </button>
                                        {panExpanded && (
                                            <button
                                                className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                onClick={() => mapRef.current?.panBy(80, 0)}
                                            >▶</button>
                                        )}
                                    </div>
                                    {panExpanded && (
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 mt-0.5"
                                            onClick={() => mapRef.current?.panBy(0, 80)}
                                        >▼</button>
                                    )}
                                </div>
                                {/* 缩放控件 */}
                                <button
                                    className="w-8 h-8 bg-white rounded-t shadow flex items-center justify-center text-lg text-gray-600 hover:bg-gray-50 leading-none"
                                    onClick={() => mapRef.current && mapRef.current.setZoom((mapRef.current.getZoom() ?? 13) + 1)}
                                >+</button>
                                <button
                                    className="w-8 h-8 bg-white rounded-b shadow flex items-center justify-center text-lg text-gray-600 hover:bg-gray-50 leading-none -mt-px"
                                    onClick={() => mapRef.current && mapRef.current.setZoom((mapRef.current.getZoom() ?? 13) - 1)}
                                >−</button>
                            </div>

                            {/* 图层开关 */}
                            <div className="absolute bottom-4 left-4 z-10 bg-white rounded-lg p-3 flex flex-col gap-2 shadow-md"
                                style={{ border: '1px solid #e5e7eb' }}>
                                {[
                                    { label: t('splitOrder.drawer.zipcodeBoundary'), icon: <span style={{ display: 'inline-block', width: 14, height: 14, background: '#7c3aed', borderRadius: 3, opacity: 0.5 }}/>, value: showZipcodeBoundary, setter: setShowZipcodeBoundary, disabled: zipcodeBoundaryData.length === 0 },
                                    { label: t('splitOrder.drawer.routeLine'), icon: <span style={{ fontSize: 14, color: '#6b7280' }}>〜</span>, value: showNavTrack && !!routePolylines, setter: setShowNavTrack, disabled: !routePolylines },
                                    { label: t('splitOrder.drawer.hideMapLabels'), icon: <span style={{ fontSize: 14, color: '#6b7280' }}>🏷</span>, value: hideMapLabels, setter: setHideMapLabels },
                                    { label: t('splitOrder.history.parcelLocation'), icon: <span style={{ fontSize: 14, color: '#6b7280' }}>📍</span>, value: showParcelLocation, setter: setShowParcelLocation },
                                    { label: t('splitOrder.history.deliveryStart'), icon: <svg width="14" height="14" viewBox="0 0 14 14"><polygon points="7,1 13,13 1,13" fill="#22c55e"/></svg>, value: showStartPoint, setter: setShowStartPoint },
                                    { label: t('splitOrder.history.deliveryEnd'), icon: <svg width="14" height="14" viewBox="0 0 14 14"><polygon points="7,1 13,13 1,13" fill="#ef4444"/></svg>, value: showEndPoint, setter: setShowEndPoint },
                                ].map(({ label, icon, value, setter, disabled }: any) => (
                                    <div key={label} className="flex items-center justify-between gap-4" style={disabled ? { opacity: 0.4 } : undefined}>
                                        <div className="flex items-center gap-2">
                                            <span className="flex items-center justify-center w-4 h-4">{icon}</span>
                                            <span className="text-xs text-gray-700 whitespace-nowrap">{label}</span>
                                        </div>
                                        <button onClick={() => !disabled && setter((v: boolean) => !v)} className="relative flex-shrink-0" style={{ width: 36, height: 20, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                            <span style={{ display: 'block', width: 36, height: 20, borderRadius: 10, background: value ? '#7c3aed' : '#d1d5db', transition: 'background 0.2s' }}/>
                                            <span style={{ position: 'absolute', top: 3, left: value ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </GoogleMap>
                    )}
                </div>
            </div>
        </Drawer>
    );
};

export default SplitRouteHistoryDrawer;
